import React, { useMemo, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EditIcon from '@mui/icons-material/Edit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  bureauOptions,
  mediumOptions,
  titelOptions,
  doelgroepOptions,
  uitingOptions,
  getDefaultOptions
} from '../utils/staticData';
import { normalizeText } from '../utils/textNormalization';
import { getTranslations } from '../utils/translations';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Custom themes (light and dark)
const lightTheme = themeQuartz.withParams({
  accentColor: "#e2452a",
  browserColorScheme: "light",
  headerFontSize: 14
});

const darkTheme = themeQuartz.withParams({
  accentColor: "#e2452a",
  backgroundColor: "#242424",
  foregroundColor: "#e5e7eb",
  browserColorScheme: "dark",
  headerBackgroundColor: "#2d2d2d",
  headerFontSize: 14,
  borderColor: "#374151",
  oddRowBackgroundColor: "#242424",
  rowHoverColor: "#2d2d2d"
});

// Action Buttons Cell Renderer Component
const ActionButtonsRenderer = (props) => {
  const handleEdit = () => {
    if (props.onEdit && props.data) {
      props.onEdit(props.data);
    }
  };

  const handleClone = () => {
    if (props.onClone && props.data) {
      props.onClone(props.data);
    }
  };

  const handleDelete = () => {
    if (props.onDelete && props.data) {
      props.onDelete(props.data);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      gap: '4px', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100%',
      padding: 0,
      margin: 0
    }}>
      <button className="btn-icon" onClick={handleEdit} title={props.editText || 'Edit'}>
        <EditIcon sx={{ fontSize: 18 }} />
      </button>
      <button className="btn-icon" onClick={handleClone} title={props.cloneText || 'Clone'}>
        <FileCopyIcon sx={{ fontSize: 18 }} />
      </button>
      <button className="btn-icon" onClick={handleDelete} title={props.deleteText || 'Delete'}>
        <DeleteIcon sx={{ fontSize: 18 }} />
      </button>
    </div>
  );
};

// Custom header checkbox that changes behavior:
// - Empty (unchecked) click: select all FILTERED rows
// - Indeterminate click: clear all selection
// - Checked click: clear all selection
const HeaderSelectAllCheckbox = (props) => {
  const checkboxRef = React.useRef(null);
  const [selectionState, setSelectionState] = React.useState('unchecked'); // 'unchecked' | 'checked' | 'indeterminate'
  const theme = useTheme();
  const checkboxAccent = theme.palette.primary.main;

  const recomputeSelectionState = React.useCallback(() => {
    const api = props.api;
    let displayedRowCount = 0;
    let selectedDisplayedRowCount = 0;

    api.forEachNodeAfterFilterAndSort((node) => {
      displayedRowCount += 1;
      if (node.isSelected && node.isSelected()) {
        selectedDisplayedRowCount += 1;
      }
    });

    if (displayedRowCount === 0 || selectedDisplayedRowCount === 0) {
      setSelectionState('unchecked');
      return;
    }

    if (selectedDisplayedRowCount === displayedRowCount) {
      setSelectionState('checked');
      return;
    }

    setSelectionState('indeterminate');
  }, [props.api]);

  useEffect(() => {
    recomputeSelectionState();
    const api = props.api;

    const onSelectionChanged = recomputeSelectionState;
    const onFilterChanged = recomputeSelectionState;
    const onModelUpdated = recomputeSelectionState;

    api.addEventListener('selectionChanged', onSelectionChanged);
    api.addEventListener('filterChanged', onFilterChanged);
    api.addEventListener('modelUpdated', onModelUpdated);

    return () => {
      api.removeEventListener('selectionChanged', onSelectionChanged);
      api.removeEventListener('filterChanged', onFilterChanged);
      api.removeEventListener('modelUpdated', onModelUpdated);
    };
  }, [props.api, recomputeSelectionState]);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = selectionState === 'indeterminate';
    }
  }, [selectionState]);

  const handleToggle = () => {
    const api = props.api;

    if (selectionState === 'unchecked') {
      // Align with previous behavior: only act on filtered rows
      api.selectAllFiltered();
    } else {
      // For both checked and indeterminate, clear all
      api.deselectAll();
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <input
        type="checkbox"
        ref={checkboxRef}
        checked={selectionState === 'checked'}
        onChange={handleToggle}
        aria-label="Select all"
        style={{ cursor: 'pointer', accentColor: checkboxAccent, backgroundColor: 'transparent', outline: 'none', boxShadow: 'none' }}
      />
    </div>
  );
};

const DataGrid = ({ 
  rowData, 
  onSelectionChanged,
  onRowDoubleClick,
  onEdit,
  onClone,
  onDelete,
  onCellValueChanged,
  fieldConfig,
  language = 'nl'
}) => {
  
  // Get translations
  const t = getTranslations(language);
  const [showFloatingFilters, setShowFloatingFilters] = React.useState(false);
  const [gridApi, setGridApi] = React.useState(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Select appropriate AG Grid theme based on MUI theme
  const agGridTheme = React.useMemo(() => {
    return isDarkMode ? darkTheme : lightTheme;
  }, [isDarkMode]);

  const normalizationFlags = fieldConfig?.textNormalization || {};

  // Generate column definitions dynamically from field config
  const columnDefs = useMemo(() => {
    if (!fieldConfig || !fieldConfig.fields) {
      // Fallback to default columns if no config
      return [
        {
          headerName: '',
          checkboxSelection: true,
          headerComponent: HeaderSelectAllCheckbox,
          width: 50,
          pinned: 'left',
          lockPosition: true,
          suppressMenu: true,
          sortable: false,
          filter: false,
          suppressSizeToFit: true
        },
        {
          field: 'Campagne',
          headerName: 'Campagne',
          flex: 1.5,
          minWidth: 120,
          editable: true,
          valueSetter: (params) => {
            const normalized = normalizeText(params.newValue || '', normalizationFlags);
            params.data.Campagne = normalized;
            return true;
          }
        },
        {
          field: 'Datum',
          headerName: 'Datum',
          flex: 1,
          minWidth: 100,
          editable: true
        },
        {
          field: 'Url',
          headerName: 'Url',
          flex: 2,
          minWidth: 150,
          editable: true,
          cellStyle: { textOverflow: 'ellipsis', overflow: 'hidden' }
        },
        {
          field: 'Bureau',
          headerName: 'Bureau',
          flex: 1,
          minWidth: 100,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: bureauOptions.filter(o => o.Id).map(o => o.Id)
          },
          valueFormatter: params => {
            const option = bureauOptions.find(o => o.Id === params.value);
            return option ? option.Name : params.value;
          }
        },
        {
          field: 'Medium',
          headerName: 'Type netwerk',
          flex: 1,
          minWidth: 100,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: mediumOptions.filter(o => o.Id).map(o => o.Id)
          },
          valueFormatter: params => {
            const option = mediumOptions.find(o => o.Id === params.value);
            return option ? option.Name : params.value;
          }
        },
        {
          field: 'Titel',
          headerName: 'Naam netwerk',
          flex: 1,
          minWidth: 100,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: titelOptions.filter(o => o.Id).map(o => o.Id)
          },
          valueFormatter: params => {
            const option = titelOptions.find(o => o.Id === params.value);
            return option ? option.Name : params.value;
          }
        },
        {
          field: 'Doelgroep',
          headerName: 'Doelgroep',
          flex: 1.2,
          minWidth: 120,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: doelgroepOptions.filter(o => o.Id).map(o => o.Id)
          },
          valueFormatter: params => {
            const option = doelgroepOptions.find(o => o.Id === params.value);
            return option ? option.Name : params.value;
          }
        },
        {
          field: 'Uiting',
          headerName: 'Type Uiting',
          flex: 1,
          minWidth: 100,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: uitingOptions.filter(o => o.Id).map(o => o.Id)
          },
          valueFormatter: params => {
            const option = uitingOptions.find(o => o.Id === params.value);
            return option ? option.Name : params.value;
          }
        },
        {
          field: 'Linknaam',
          headerName: 'Naam uiting',
          flex: 1,
          minWidth: 100,
          editable: true,
          valueSetter: (params) => {
            const normalized = normalizeText(params.newValue || '', normalizationFlags);
            params.data.Linknaam = normalized;
            return true;
          }
        },
        {
          headerName: t.actions,
          width: 110,
          pinned: 'right',
          cellRenderer: ActionButtonsRenderer,
          cellRendererParams: {
            onEdit: onEdit,
            onClone: onClone,
            onDelete: onDelete,
            editText: t.edit,
            cloneText: t.clone,
            deleteText: t.delete
          },
          sortable: false,
          filter: false,
          suppressSizeToFit: true,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
          }
        }
      ];
    }

    // Build columns array with checkbox first
    const columns = [
      {
        headerName: '',
        checkboxSelection: true,
        headerComponent: HeaderSelectAllCheckbox,
        width: 50,
        pinned: 'left',
        lockPosition: true,
        suppressMenu: true,
        sortable: false,
        filter: false,
        suppressSizeToFit: true
      }
    ];

    // Get enabled fields and sort by gridOrder
    const enabledFields = Object.keys(fieldConfig.fields)
      .filter(fieldName => fieldConfig.fields[fieldName].enabled)
      .map(fieldName => ({
        name: fieldName,
        config: fieldConfig.fields[fieldName]
      }))
      .sort((a, b) => a.config.gridOrder - b.config.gridOrder);

    // Create column definition for each enabled field
    enabledFields.forEach(({ name: fieldName, config }) => {
      const colDef = {
        field: fieldName,
        headerName: config.displayName,
        flex: config.gridWidth,
        minWidth: 100,
        editable: true
      };

      // Special styling for URL field
      if (fieldName === 'Url') {
        colDef.cellStyle = { textOverflow: 'ellipsis', overflow: 'hidden' };
      }

      // Configure select fields
      if (config.fieldType === 'select' && config.options && config.options.length > 0) {
        colDef.cellEditor = 'agSelectCellEditor';
        colDef.cellEditorParams = {
          values: config.options.filter(o => o.Id).map(o => o.Id)
        };
        colDef.valueFormatter = params => {
          const option = config.options.find(o => o.Id === params.value);
          return option ? option.Name : params.value;
        };
      }

      // Normalize text fields (except Url) on commit
      if (config.fieldType === 'text' && fieldName !== 'Url') {
        colDef.valueSetter = (params) => {
          const normalized = normalizeText(params.newValue || '', normalizationFlags);
          params.data[fieldName] = normalized;
          return true;
        };
      }

      columns.push(colDef);
    });

    // Add actions column at the end
    columns.push({
      headerName: t.actions,
      width: 110,
      pinned: 'right',
      cellRenderer: ActionButtonsRenderer,
      cellRendererParams: {
        onEdit: onEdit,
        onClone: onClone,
        onDelete: onDelete,
        editText: t.edit,
        cloneText: t.clone,
        deleteText: t.delete
      },
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
      }
    });

    return columns;
  }, [fieldConfig, onEdit, onClone, onDelete, t]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: showFloatingFilters,
    singleClickEdit: true
  }), [showFloatingFilters]);

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  // Force grid refresh when theme changes
  useEffect(() => {
    if (gridApi) {
      gridApi.refreshCells({ force: true });
    }
  }, [isDarkMode, gridApi]);

  const handleFilterToggle = () => {
    if (showFloatingFilters) {
      // Als filters zichtbaar zijn: reset EN verberg ze
      if (gridApi) {
        gridApi.setFilterModel(null);
        // Clear selection when filters are reset to avoid confusion
        gridApi.deselectAll();
      }
      setShowFloatingFilters(false);
    } else {
      // Als filters niet zichtbaar zijn: toon ze
      setShowFloatingFilters(true);
    }
  };

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <div style={{ 
        position: 'absolute', 
        top: '8px', 
        right: '8px', 
        zIndex: 10
      }}>
        <button 
          onClick={handleFilterToggle}
          className="btn-icon"
          title={showFloatingFilters ? t.hideResetFilters : t.showFilters}
          style={{ padding: '6px' }}
        >
          {showFloatingFilters ? (
            <FilterAltOffIcon sx={{ fontSize: 20 }} />
          ) : (
            <FilterAltIcon sx={{ fontSize: 20 }} />
          )}
        </button>
      </div>
      <AgGridReact
        key={`ag-grid-${isDarkMode ? 'dark' : 'light'}`}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowSelection="multiple"
        suppressRowClickSelection={true}
        onSelectionChanged={onSelectionChanged}
        onRowDoubleClicked={onRowDoubleClick}
        onCellValueChanged={onCellValueChanged}
        onGridReady={onGridReady}
        enableCellTextSelection={true}
        ensureDomOrder={true}
        animateRows={true}
        theme={agGridTheme}
        stopEditingWhenCellsLoseFocus={true}
        columnHoverHighlight={true}
      />
    </div>
  );
};

export default DataGrid;

